import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";
import { Percent, Save, Trash2, Search, Loader2, UserCog } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommissionSettingsPage() {
  const { token } = useAuth();
  const [defaultRate, setDefaultRate] = useState(5);
  const [userRates, setUserRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newUserId, setNewUserId] = useState("");
  const [newRate, setNewRate] = useState("");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/api/admin/commission-settings`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setDefaultRate(d.default_rate);
        setUserRates(d.user_rates);
      }
    } catch {} finally { setLoading(false); }
  };

  const saveDefault = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/commission-settings/default`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ rate: parseFloat(defaultRate) }),
      });
      if (res.ok) toast.success("Taux par defaut mis a jour");
      else toast.error("Erreur");
    } catch { toast.error("Erreur reseau"); }
    setSaving(false);
  };

  const searchUsers = async (q) => {
    setSearch(q);
    if (q.length < 2) { setUsers([]); return; }
    try {
      const res = await fetch(`${API}/api/admin/users?search=${encodeURIComponent(q)}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        setUsers(Array.isArray(d) ? d.slice(0, 5) : (d.users || []).slice(0, 5));
      }
    } catch {}
  };

  const addUserRate = async (userId) => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error("Taux invalide"); return; }
    try {
      const res = await fetch(`${API}/api/admin/commission-settings/user/${userId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ rate }),
      });
      if (res.ok) { toast.success("Taux ajoute"); fetchData(); setShowAdd(false); setSearch(""); setNewRate(""); }
      else toast.error("Erreur");
    } catch { toast.error("Erreur reseau"); }
  };

  const deleteUserRate = async (userId) => {
    try {
      const res = await fetch(`${API}/api/admin/commission-settings/user/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { toast.success("Taux personnalise supprime"); fetchData(); }
    } catch { toast.error("Erreur reseau"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-[#FF6B35]" /></div>;

  return (
    <div className="space-y-6" data-testid="commission-settings-page">
      <div className="flex items-center gap-3">
        <Percent className="w-7 h-7 text-[#FF6B35]" />
        <h1 className="text-2xl font-bold text-white">Commissions</h1>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Taux par defaut</h2>
        <div className="flex items-center gap-3">
          <input type="number" min="0" max="100" step="0.5" value={defaultRate}
            onChange={(e) => setDefaultRate(e.target.value)}
            className="w-24 bg-[#121212] border border-[#333] rounded-lg px-3 py-2 text-white text-sm text-center focus:border-[#FF6B35] outline-none"
            data-testid="default-rate-input" />
          <span className="text-white text-lg">%</span>
          <button onClick={saveDefault} disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            data-testid="save-default-rate-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Sauvegarder
          </button>
        </div>
        <p className="text-xs text-[#888] mt-2">Ce taux s'applique a tous les organisateurs sauf ceux avec un taux personnalise.</p>
      </div>

      <div className="bg-[#1a1a1a] border border-[#333] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white">Taux personnalises</h2>
          <button onClick={() => setShowAdd(!showAdd)}
            className="px-3 py-1.5 bg-[#252525] hover:bg-[#333] text-white text-sm rounded-lg border border-[#444]"
            data-testid="add-user-rate-btn">
            + Ajouter
          </button>
        </div>

        {showAdd && (
          <div className="bg-[#121212] border border-[#333] rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-[#888]" />
              <input value={search} onChange={(e) => searchUsers(e.target.value)}
                placeholder="Rechercher un utilisateur..."
                className="flex-1 bg-transparent text-white text-sm outline-none placeholder-[#666]"
                data-testid="search-user-input" />
            </div>
            {users.length > 0 && (
              <div className="space-y-1">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between bg-[#1a1a1a] rounded px-3 py-2 text-sm">
                    <div>
                      <span className="text-white">{u.name || u.pseudo}</span>
                      <span className="text-[#888] ml-2">{u.email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <input type="number" min="0" max="100" step="0.5" value={newRate}
                        onChange={(e) => setNewRate(e.target.value)} placeholder="%"
                        className="w-16 bg-[#121212] border border-[#333] rounded px-2 py-1 text-white text-sm text-center" />
                      <button onClick={() => addUserRate(u.id)}
                        className="px-2 py-1 bg-[#FF6B35] text-white rounded text-xs">OK</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {userRates.length === 0 ? (
          <p className="text-sm text-[#888]">Aucun taux personnalise configure.</p>
        ) : (
          <div className="space-y-2">
            {userRates.map((ur) => (
              <div key={ur.user_id} className="flex items-center justify-between bg-[#121212] rounded-lg px-4 py-3 border border-[#252525]">
                <div className="flex items-center gap-3">
                  <UserCog className="w-4 h-4 text-[#FF6B35]" />
                  <div>
                    <p className="text-sm text-white">{ur.user_name || ur.user_pseudo || ur.user_id}</p>
                    <p className="text-xs text-[#888]">{ur.user_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-lg font-bold text-[#FF6B35]">{ur.rate}%</span>
                  <button onClick={() => deleteUserRate(ur.user_id)}
                    className="p-1.5 hover:bg-red-500/20 rounded text-red-400"
                    data-testid={`delete-rate-${ur.user_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
